/**
 * Client-side config accessor. Reads from window.__APP_CONFIG__ (injected by server at runtime)
 * with fallback defaults for local development.
 */

export interface RegionInfo {
  airportCode: string;
  regionName: string;
  publicRegion: string;
  partition: string;
  isBorderRegion: boolean;
  isOptInRegion?: boolean;
  clientId?: string;
}

export interface TumblerEndpoints {
  tumblerApi: string;
  admiral: string;
  elbAtlas: string;
  sdpds: string;
  cloudwatch: string;
  mechanicExplorer?: string;
  mechanic?: string;
  sshWeb?: string;
  ticketing?: string;
  apollo?: string;
  isengard?: string;
}

export interface TumblerConfig {
  current: RegionInfo;
  stage: string;
  stageRegions: { [stage: string]: RegionInfo[] };
  endpoints: TumblerEndpoints;
}

declare global {
  interface Window {
    __APP_CONFIG__?: TumblerConfig;
  }
}

export const DEFAULT_CONFIG: TumblerConfig = {
  current: { airportCode: 'dub', regionName: 'Europe (Ireland)', publicRegion: 'eu-west-1', partition: 'aws', isBorderRegion: false },
  stage: 'beta',
  stageRegions: {},
  endpoints: {
    tumblerApi: 'https://api.eu-west-1.tumbler.beta.search-services.aws.a2z.com',
    admiral: 'https://admiral-dub.ec2.amazon.com',
    elbAtlas: 'https://elb-atlas-dub.amazon.com',
    sdpds: 'api.eu-west-1.sdpds.beta.search-services.aws.a2z.com',
    cloudwatch: 'monitoring.eu-west-1.amazonaws.com',
    mechanicExplorer: 'https://aescloudopstools-mechanic-iad.aka.amazon.com/explorer',
    mechanic: 'https://mechanic.amazon.dev',
    sshWeb: 'https://ssh.corp.amazon.com',
    ticketing: 'https://t.corp.amazon.com',
    apollo: 'https://apollo.amazon.com',
    isengard: 'https://isengard.amazon.com',
  },
};

/** Returns the full tumbler config from CDK-injected window.__APP_CONFIG__. */
export function getTumblerConfig(): TumblerConfig {
  // Guard `window` so this is safe in non-browser contexts (Node/jest, SSR).
  // Falls back to DEFAULT_CONFIG when no runtime config has been injected.
  if (typeof window !== 'undefined' && window.__APP_CONFIG__) {
    return window.__APP_CONFIG__;
  }
  return DEFAULT_CONFIG;
}

/** All regions for the current stage. */
export function getAllRegions(): RegionInfo[] {
  const config = getTumblerConfig();
  return config.stageRegions[config.stage] || [];
}

/** Lookup a region by airport code (case-insensitive). Falls back to current. */
export function getRegion(airportCode: string): RegionInfo {
  const config = getTumblerConfig();
  const allRegions = Object.values(config.stageRegions).flat();
  const match = allRegions.find(
    r => r.airportCode.toLowerCase() === airportCode.toLowerCase()
  );
  if (match) return match;

  return config.current;
}

/** Lookup a region by its public region name (e.g. 'us-east-1'). */
export function getRegionByPublicRegion(publicRegion: string): RegionInfo | undefined {
  const config = getTumblerConfig();
  const allRegions = Object.values(config.stageRegions).flat();
  return allRegions.find(r => r.publicRegion === publicRegion)
    ?? (config.current.publicRegion === publicRegion ? config.current : undefined);
}

/** Returns the endpoints map from the CDK-injected config, merged over DEFAULT_CONFIG so every field falls back per-field. */
export function getEndpoints(): TumblerEndpoints {
  return { ...DEFAULT_CONFIG.endpoints, ...getTumblerConfig().endpoints };
}
